import React, { useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, History, X, MessageSquare } from "lucide-react";
import { toast } from "../ui/Toast";

// Admin PIP oversight tab (mounted in AdminPerformancePage). Scope: the HR
// APPROVE gate + org-wide read-only oversight + check-in history. It does NOT
// offer check-in / outcome — those stay in the manager view (ManagerPIPView)
// so the two surfaces don't duplicate the same action inconsistently. Admins
// who also manage reports use that view for check-in/outcome.

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

// Mirror of the Session B PIP badge maps (kept in sync — lowercase vocab).
const STATUS_STYLE = {
  draft:      "text-white/50 bg-white/5 border-white/15",
  active:     "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  extended:   "text-amber-400 bg-amber-500/10 border-amber-500/25",
  completed:  "text-blue-400 bg-blue-500/10 border-blue-500/20",
  terminated: "text-red-400 bg-red-500/10 border-red-500/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";
const STATUS_LABEL = { draft: "Draft", active: "Active", extended: "Extended", completed: "Completed", terminated: "Terminated" };
const RATING_STYLE = {
  on_track:          "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  needs_improvement: "text-amber-400 bg-amber-500/10 border-amber-500/25",
  not_meeting:       "text-red-400 bg-red-500/10 border-red-500/20",
};
const RATING_LABEL = { on_track: "On Track", needs_improvement: "Needs Improvement", not_meeting: "Not Meeting" };
const TERMINAL = new Set(["completed", "terminated"]);

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const badge = (map, key, label) => (
  <span className={`text-xs px-2 py-0.5 rounded-full border ${map[key] || STATUS_FALLBACK}`}>{label || key || "—"}</span>
);

const FILTERS = ["ALL", "draft", "active", "extended", "completed", "terminated"];

export default function PIPTab() {
  const [pips, setPips]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState("ALL");
  const [approving, setApproving] = useState(null);
  const [openId, setOpenId]   = useState(null);          // history drawer
  const [history, setHistory] = useState({});            // { [id]: [checkins] }

  const token = localStorage.getItem("token");
  const authH = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/performance/pip`, { headers: authH });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPips(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message); setPips([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const approve = async (id) => {
    setApproving(id);
    try {
      const res = await fetch(`${API_BASE}/api/performance/pip/${id}/approve`, {
        method: "PATCH", headers: authH,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      // Backend notifies the employee on approve. Reflect the new status.
      setPips(ps => ps.map(p => (p.id === id ? { ...p, ...updated } : p)));
      toast.success("PIP approved — employee notified");
    } catch { toast.error("Failed to approve PIP"); }
    finally { setApproving(null); }
  };

  const loadHistory = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/performance/pip/${id}/checkins`, { headers: authH });
      const data = res.ok ? await res.json() : [];
      setHistory(h => ({ ...h, [id]: Array.isArray(data) ? data : [] }));
    } catch { setHistory(h => ({ ...h, [id]: [] })); }
  };

  const toggleHistory = (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (!history[id]) loadHistory(id);
  };

  // Draft-first (needs the admin's approval), then keep the API's newest-first order.
  const ordered = [...pips]
    .filter(p => filter === "ALL" || p.status === filter)
    .sort((a, b) => (a.status === "draft" ? 0 : 1) - (b.status === "draft" ? 0 : 1));

  const draftCount = pips.filter(p => p.status === "draft").length;

  return (
    <div className="space-y-5">
      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

      {/* Filter chips */}
      <div className="flex gap-1.5 flex-wrap">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors capitalize ${
              filter === f
                ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]"
                : "bg-transparent border-white/10 text-white/40 hover:border-[#00ff90]/20 hover:text-white/60"
            }`}>
            {f === "ALL" ? "All" : STATUS_LABEL[f]}
            {f === "draft" && draftCount > 0 && <span className="ml-1 text-amber-400">({draftCount})</span>}
          </button>
        ))}
      </div>

      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <ShieldAlert size={14} className="text-amber-400" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Improvement Plans</span>
          {draftCount > 0 && <span className="text-xs text-amber-400/70">· {draftCount} awaiting approval</span>}
        </div>

        <div className="divide-y divide-white/5">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => <div key={i} className="px-5 py-4"><div className="h-12 rounded-lg bg-white/5 animate-pulse" /></div>)
          ) : ordered.length === 0 ? (
            <p className="px-5 py-8 text-white/20 text-sm italic text-center">No PIPs{filter !== "ALL" ? ` with status "${STATUS_LABEL[filter]}"` : ""}.</p>
          ) : ordered.map(p => (
            <div key={p.id}>
              <div className="flex items-start justify-between gap-3 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="text-sm text-white/80 font-medium">{p.employee_name || p.employee_id}</p>
                    {badge(STATUS_STYLE, p.status, STATUS_LABEL[p.status])}
                  </div>
                  <p className="text-xs text-white/40">{p.title}</p>
                  <p className="text-xs text-white/25 mt-0.5">
                    Manager: {p.manager_name || p.manager_id || "—"}
                    {" · "}{fmtDate(p.start_date)} → {fmtDate(p.review_date)} → {fmtDate(p.end_date)}
                    {" · "}{p.check_in_frequency}
                  </p>
                  {TERMINAL.has(p.status) && p.outcome && (
                    <p className="text-xs text-white/40 mt-0.5">Outcome: <span className="text-white/60">{p.outcome}</span>{p.outcome_notes ? ` — ${p.outcome_notes}` : ""}</p>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                  {/* Approve is offered ONLY on draft. The backend's approve is
                      unconditional (would flip a completed PIP back to active),
                      so the FE must never expose it outside draft. */}
                  {p.status === "draft" && (
                    <button onClick={() => approve(p.id)} disabled={approving === p.id}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20 disabled:opacity-40 transition-colors">
                      <ShieldCheck size={12} /> {approving === p.id ? "Approving…" : "Approve"}
                    </button>
                  )}
                  <button onClick={() => toggleHistory(p.id)}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">
                    <History size={12} /> History
                  </button>
                </div>
              </div>

              {/* Check-in history drawer */}
              {openId === p.id && (
                <div className="mx-5 mb-4 rounded-xl border border-[#00ff90]/15 bg-[#00ff90]/3 p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-[#00ff90]/60 uppercase tracking-wider flex items-center gap-1">
                      <MessageSquare size={11} /> Check-in History
                    </p>
                    <button onClick={() => setOpenId(null)} className="text-white/30 hover:text-white transition-colors"><X size={12} /></button>
                  </div>
                  {!history[p.id] ? (
                    <p className="text-xs text-white/25 italic">Loading…</p>
                  ) : history[p.id].length === 0 ? (
                    <p className="text-xs text-white/25 italic">No check-ins recorded yet.</p>
                  ) : history[p.id].map(c => (
                    <div key={c.id} className="rounded-lg border border-white/5 bg-white/3 p-3 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        {badge(RATING_STYLE, c.rating, RATING_LABEL[c.rating])}
                        <span className="text-xs text-white/25">{fmtDate(c.completed_at || c.created_at)}</span>
                      </div>
                      {c.manager_notes && <p className="text-xs text-white/60"><span className="text-white/30">Manager: </span>{c.manager_notes}</p>}
                      {c.employee_response && <p className="text-xs text-white/60"><span className="text-white/30">Employee: </span>{c.employee_response}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
